import { useSWR, useSWRMutation } from '@tiger/swr';
import { Button, Empty, Form, Skeleton } from 'antd';
import { $Formutil, withForm } from 'react-antd-formutil';
import { Link, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import Card, { CardButtonWrap } from 'components/Card';
import ErrorBox from 'components/ErrorBox';
import FileUploader, { FileItemType } from 'components/FileUploader/UploaderList';
import { useAlertModal, useConfirmModal } from 'components/Modal';
import Toast from 'components/Toast';
import FullTransferDetail from 'modules/Transfer/components/TransferDetail/Full';
import PartialTransferDetail from 'modules/Transfer/components/TransferDetail/Partial';
import TransferFrom from 'modules/Transfer/components/TransferFrom';
import { TransferMethodEnum } from 'modules/Transfer/type';
import APP from 'stores/APP';
import security from 'stores/security';
import API from 'utils/API';
import { isEmptyValue } from 'utils/helper';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import { statusMap, TransferDetailItem } from '../List';
import outerStyles from '../style.scss';
import styles from './style.scss';

interface DetailProps {
    $formutil: $Formutil<{
        newFiles: FileItemType[];
    }>;
}

const Detail: React.FC<DetailProps> = ({ $formutil }) => {
    const { open } = useConfirmModal();
    const { open: openAlertModal } = useAlertModal();
    const navigate = useNavigate();
    const id = useParams().id!;
    const { $params, $setValues } = $formutil;
    const detailSwrKey = {
        url: API.CUSTOMER.transfer.transferSearch(id),
        params: {
            account_id: security.currentAccountId
        }
    };
    const { isLoading, error, data, mutate } = useSWR<TransferDetailItem>(detailSwrKey, {
        dataChain: res => res.data
    });
    const { trigger: transferWithdraw } = useSWRMutation(detailSwrKey, (id: TransferDetailItem['id']) =>
        http.post(
            API.CUSTOMER.transfer.transferWithdraw(),
            { id },
            {
                params: {
                    account_id: security.currentAccountId
                }
            }
        )
    );
    const { isMutating: updateTransferLoading, trigger: updateTransfer } = useSWRMutation(
        `${API.CUSTOMER.transfer.updateTransfer(id)}`,
        (newFiles: string[]) =>
            http.post(
                API.CUSTOMER.transfer.updateTransfer(id),
                {
                    newFiles
                },
                {
                    params: {
                        account_id: security.currentAccountId
                    }
                }
            ),
        {
            onSuccess: () => {
                Toast.show(__('提交成功！'));

                // 上传成功之后，清除已上传的文件
                $setValues({
                    newFiles: [{ name: '', url: '', file: undefined }]
                });
            },
            onError: error => Toast.show(error.message)
        }
    );

    const withdrawal = async () => {
        Toast.loading(true);

        try {
            await transferWithdraw(data!.id);

            Toast.loading(false);
            Toast.show(__('撤销成功'));
            navigate('..', { replace: true });
        } catch (error) {
            Toast.loading(false);
            Toast.show(error);
        }
    };

    async function handleWithdrawal() {
        open({
            content: printf(
                __('Are you sure you want to withdraw the request for position transfer from %s?'),
                data?.company || ''
            ),
            okText: __('Yes'),
            cancelText: __('No'),
            onOk: withdrawal
        });
    }

    const handleSubmit = () => {
        if ((data?.file_urls?.length || 0) + ($params.newFiles?.filter(({ url }) => Boolean(url)).length || 0) > 6) {
            openAlertModal({
                title: __('Oops! '),
                content: (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: printf(
                                __(
                                    `You've exceeded the 6 file limit for upload. Please email us at %s if you would like to submit additional proof.`
                                ),
                                `<a href="mailto:support@tradeup.com">support@tradeup.com</a>`
                            )
                        }}
                    />
                )
            });
        } else {
            updateTransfer($params.newFiles.map(({ url }) => url).filter(Boolean) as string[]);
        }
    };

    const render = () => {
        if (error && !data) {
            return <ErrorBox error={error} onClick={() => mutate()} />;
        }

        if (isLoading) {
            return (
                <>
                    <Skeleton active title={{ width: '50%' }} paragraph={false} />
                    <Skeleton active title={{ width: '30%' }} paragraph={false} />
                    <Skeleton active title={{ width: '40%' }} paragraph={false} />
                    <Skeleton.Button
                        active
                        block
                        style={{
                            height: 200,
                            marginBottom: 15
                        }}
                    />
                    <Skeleton active title={{ width: '30%' }} paragraph={false} />
                    <Skeleton
                        active
                        title={false}
                        paragraph={{
                            rows: 1,
                            width: '80%'
                        }}
                    />
                    <Skeleton.Button
                        active
                        style={{
                            width: 200,
                            height: 150
                        }}
                    />
                </>
            );
        }

        if (!data) {
            return <Empty className="mt-5" description={__('no data')} />;
        }

        const {
            createdAt,
            status,
            broker,
            broker_id: brokerId,
            broker_user_name: brokerUerName,
            broker_account: brokerAccount,
            transfer_method: transferMethod,
            transfer_property_infos: transferPropertyInfos,
            account_no: accountNo,
            mktCashValue,
            mktShortValue
        } = data;
        const isReceived = status === 'Received';
        const { className, text } = statusMap[status] || {};
        const isPartial = transferMethod === TransferMethodEnum.ACATS_PARTIAL;

        return (
            <>
                <div className={styles.request}>
                    {printf(
                        __('Your transfer request initiated on %s'),
                        dayjs(createdAt).format(APP.isEn ? 'ddd, MMMD, YYYY h:m a' : 'YYYY年MM月DD日 HH:MM:ss')
                    )}
                </div>
                {isReceived ? (
                    <Form className={styles.uploadContainer} layout="vertical" onFinish={handleSubmit}>
                        <div className={styles.statement}>
                            <div className={styles.title}>
                                {security.isIRAAccount
                                    ? __('Upload Recent Statement and Photo ID')
                                    : __('Upload Recent Statement')}
                            </div>
                            <FileUploader
                                maxCount={6}
                                type="deposit"
                                name="newFiles"
                                extra={
                                    security.isIRAAccount
                                        ? printf(
                                              __(
                                                  'Please upload your most recent %s statement, along with valid photo identification. If you do not have these documents on hand, please return later to upload the requested documentation.'
                                              ),
                                              broker
                                          )
                                        : printf(
                                              __(
                                                  'Please upload your most recent %s statement. If you do not have this document on hand, please return later to upload the requested documentation.'
                                              ),
                                              broker
                                          )
                                }
                                bucket="customer-deposit"
                            />
                        </div>
                        <CardButtonWrap>
                            <Button
                                size="large"
                                htmlType="submit"
                                type="primary"
                                loading={updateTransferLoading}
                                disabled={!$params.newFiles?.[0]?.url}>
                                {__('Submit')}
                            </Button>
                        </CardButtonWrap>
                    </Form>
                ) : null}
                <div>
                    <h3 className={styles.title}>{__('Transfer Status')}</h3>
                    <div className={styles.transferDetail}>
                        <span className="text-muted mr-3">{__('Transfer Status')}</span>
                        <span className={className || ''}>{text}</span>
                    </div>
                    {isPartial ? null : (
                        <div className={styles.transferDetail}>
                            <span className="text-muted mr-3">{__('Transfer Instructions')}</span>
                            <span>{__('Transfer the entire account in kind')}</span>
                        </div>
                    )}
                </div>
                <TransferFrom
                    brokerInfo={{
                        brokerId: Number(brokerId),
                        holderName: brokerUerName,
                        account: brokerAccount
                    }}
                    linkOptions={
                        isReceived
                            ? {
                                  text: __('Withdrawal Request'),
                                  onClickLink: handleWithdrawal
                              }
                            : undefined
                    }
                />
                {isPartial ? (
                    <PartialTransferDetail
                        transferPropertyInfos={transferPropertyInfos || []}
                        /**
                         * mktCashValue是做多现金, 如果存在直接展示原值, 但是这里mktCashValue是可能为0的需要注意
                         * mktShortValue是做空现金, 接口返回的是绝对值, 如果存在需要取负数展示
                         * 如果两者都没有, 则不展示现金信息
                         * 两者只能存在一个
                         */
                        cashAmount={
                            !isEmptyValue(mktCashValue) ? mktCashValue : mktShortValue ? -mktShortValue : undefined
                        }
                    />
                ) : (
                    <FullTransferDetail accountNo={accountNo} />
                )}
                {isReceived ? null : (
                    <CardButtonWrap className="mt-5">
                        <Link to="..">
                            <Button size="large" type="primary">
                                {__('Back')}
                            </Button>
                        </Link>
                    </CardButtonWrap>
                )}
            </>
        );
    };

    return (
        <Card form className={styles.root}>
            <h3 className={outerStyles.historyTitle}>{__('Transfer History')}</h3>
            {render()}
        </Card>
    );
};

export default withForm(Detail);
